// === Admin: Program Settings Lists ===
// Two-column layout: category filters + item list with search
// Ported from old Click2Gro (ProgramSettingsListsController.php)

import { useEffect, useState, useCallback, useMemo } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  Snackbar,
  InputAdornment,
  Chip,
  FormControl,
  InputLabel,
  Grid,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon,
  ArrowBack as BackIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import {
  AppTableHead,
  AppTableHeadCell,
  AppTableRow,
} from '../components/AppTable';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CategoryOption {
  readonly id: number;
  readonly category: string;
}

interface ProgramSettingsListRow {
  readonly id: number;
  readonly description: string;
  readonly category_id: number | null;
  readonly program_settings_category: { readonly id: number; readonly category: string } | null;
  readonly created_at: string | null;
  readonly updated_at: string | null;
}

interface FormState {
  description: string;
  category_id: number | '';
}

const DEFAULT_FORM: FormState = { description: '', category_id: '' };

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function getApiUrl(): string {
  return (import.meta.env['VITE_API_URL'] as string | undefined) ?? 'http://localhost:8787';
}

function authHeaders(token: string): Record<string, string> {
  return { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' };
}

function fmtDate(iso: string | null): string {
  if (!iso) return '—';
  return new Intl.DateTimeFormat('en-AU', { dateStyle: 'medium' }).format(new Date(iso));
}

// ---------------------------------------------------------------------------
// Page component
// ---------------------------------------------------------------------------
export function ProgramSettingsListsPage(): React.ReactElement {
  const navigate = useNavigate();
  const { session } = useAuth();
  const token = session?.access_token ?? '';

  // ----- Category data and selected category -----
  const [categories, setCategories] = useState<CategoryOption[]>([]);
  const [selectedCategoryId, setSelectedCategoryId] = useState<number | null>(null);

  // ----- List data -----
  const [rows, setRows] = useState<ProgramSettingsListRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [listError, setListError] = useState<string | null>(null);

  // ----- Search -----
  const [search, setSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(search), 350);
    return () => clearTimeout(timer);
  }, [search]);

  // ----- Form (Add/Edit) -----
  const [form, setForm] = useState<FormState>(DEFAULT_FORM);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formError, setFormError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  // ----- Delete confirmation -----
  const [deleteId, setDeleteId] = useState<number | null>(null);
  const [deleteLabel, setDeleteLabel] = useState('');
  const [deleting, setDeleting] = useState(false);

  // ----- Snackbar -----
  const [snackOpen, setSnackOpen] = useState(false);
  const [snackMsg, setSnackMsg] = useState('');

  const showSnack = (msg: string): void => {
    setSnackMsg(msg);
    setSnackOpen(true);
  };

  // ---------------------------------------------------------------------------
  // Fetch categories
  // ---------------------------------------------------------------------------
  useEffect(() => {
    if (!token) return;
    void (async () => {
      try {
        const res = await fetch(`${getApiUrl()}/api/v1/admin/program-settings-lists/categories`, {
          headers: authHeaders(token),
        });
        if (!res.ok) return;
        const json = (await res.json()) as { data: CategoryOption[] };
        setCategories(json.data);
        // Auto-select first category on load
        if (json.data.length > 0) {
          setSelectedCategoryId(json.data[0]!.id);
        }
      } catch { /* ignore */ }
    })();
  }, [token]);

  // ---------------------------------------------------------------------------
  // Fetch list by category
  // ---------------------------------------------------------------------------
  const fetchList = useCallback(async () => {
    if (!token || selectedCategoryId === null) return;
    setLoading(true);
    setListError(null);
    try {
      const url = new URL(`${getApiUrl()}/api/v1/admin/program-settings-lists`);
      url.searchParams.set('category_id', String(selectedCategoryId));
      if (debouncedSearch) url.searchParams.set('search', debouncedSearch);

      const res = await fetch(url.toString(), { headers: authHeaders(token) });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = (await res.json()) as { data: ProgramSettingsListRow[]; total: number };
      setRows(json.data);
      setTotal(json.total);
    } catch (e) {
      setListError(e instanceof Error ? e.message : 'Failed to load records');
    } finally {
      setLoading(false);
    }
  }, [token, selectedCategoryId, debouncedSearch]);

  useEffect(() => {
    void fetchList();
  }, [fetchList]);

  // ---------------------------------------------------------------------------
  // Item counts per category (cached)
  // ---------------------------------------------------------------------------
  const itemCounts = useMemo(() => {
    const counts: Record<number, number> = {};
    rows.forEach((row) => {
      if (row.category_id) {
        counts[row.category_id] = (counts[row.category_id] ?? 0) + 1;
      }
    });
    return counts;
  }, [rows]);

  // ---------------------------------------------------------------------------
  // Open Add / Edit
  // ---------------------------------------------------------------------------
  const openAdd = () => {
    setEditingId(null);
    // Pre-fill category with selected one
    setForm({ description: '', category_id: selectedCategoryId || '' });
    setFormError(null);
    setDialogOpen(true);
  };

  const openEdit = async (id: number) => {
    if (!token) return;
    setFormError(null);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/program-settings-lists/${id}`, {
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error('Failed to load record');
      const json = (await res.json()) as { data: ProgramSettingsListRow };
      const r = json.data;
      setForm({
        description: r.description,
        category_id: r.category_id ?? '',
      });
      setEditingId(id);
      setDialogOpen(true);
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Failed to load record');
    }
  };

  // ---------------------------------------------------------------------------
  // Save
  // ---------------------------------------------------------------------------
  const handleSave = async () => {
    const trimmed = form.description.trim();
    if (!trimmed) { setFormError('Description is required.'); return; }
    if (trimmed.length > 300) { setFormError('Description must be 300 characters or fewer.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const url = editingId
        ? `${getApiUrl()}/api/v1/admin/program-settings-lists/${editingId}`
        : `${getApiUrl()}/api/v1/admin/program-settings-lists`;
      const res = await fetch(url, {
        method: editingId ? 'PUT' : 'POST',
        headers: authHeaders(token),
        body: JSON.stringify({
          description: trimmed,
          category_id: form.category_id === '' ? null : form.category_id,
        }),
      });
      if (!res.ok) {
        const err = (await res.json()) as { error?: string };
        throw new Error(err.error ?? `HTTP ${res.status}`);
      }
      setDialogOpen(false);
      showSnack(editingId ? 'Record updated.' : 'Record added.');
      void fetchList();
    } catch (e) {
      setFormError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Delete
  // ---------------------------------------------------------------------------
  const confirmDelete = (id: number, label: string) => { setDeleteId(id); setDeleteLabel(label); };

  const handleDelete = async () => {
    if (!deleteId || !token) return;
    setDeleting(true);
    try {
      const res = await fetch(`${getApiUrl()}/api/v1/admin/program-settings-lists/${deleteId}`, {
        method: 'DELETE',
        headers: authHeaders(token),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDeleteId(null);
      showSnack('Record deleted.');
      void fetchList();
    } catch (e) {
      showSnack(e instanceof Error ? e.message : 'Delete failed');
    } finally {
      setDeleting(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------
  const selectedCategory = categories.find((c) => c.id === selectedCategoryId);

  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 4, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>Program Settings</Typography>
            <Typography variant="body2" color="text.secondary">
              Manage and configure program reference data
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={openAdd}>
            Add Item
          </Button>
        </Box>

        {/* Two-Column Layout */}
        <Grid container spacing={3}>

          {/* LEFT COLUMN: Category Buttons + Search */}
          <Grid item xs={12} md={3}>
            <Paper sx={{ p: 2 }}>
              <Typography 
                variant="subtitle2" 
                fontWeight={600} 
                sx={{ 
                  mb: 2, 
                  opacity: 0.85, 
                  textTransform: 'uppercase', 
                  fontSize: '0.75rem', 
                  letterSpacing: '0.5px' 
                }}
              >
                Select Category to Add
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                {categories.map((cat) => (
                  <Button
                    key={cat.id}
                    variant={selectedCategoryId === cat.id ? 'contained' : 'outlined'}
                    onClick={() => { setSelectedCategoryId(cat.id); setSearch(''); }}
                    fullWidth
                    sx={{
                      justifyContent: 'flex-start',
                      px: 1.5,
                      py: 1,
                      textTransform: 'none',
                      fontSize: '0.95rem',
                      ...(selectedCategoryId === cat.id && {
                        backgroundColor: 'primary.main',
                        color: 'primary.contrastText',
                        '&:hover': { backgroundColor: 'primary.dark' },
                      }),
                    }}
                  >
                    <span>{cat.category}</span>
                    <span style={{ marginLeft: 'auto', opacity: 0.7, fontSize: '0.85rem' }}>
                      ({itemCounts[cat.id] ?? 0})
                    </span>
                  </Button>
                ))}
              </Box>

              {/* Search in selected category */}
              <Box sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                <Typography 
                  variant="subtitle2" 
                  fontWeight={600} 
                  sx={{ 
                    mb: 1, 
                    opacity: 0.85, 
                    textTransform: 'uppercase', 
                    fontSize: '0.75rem', 
                    letterSpacing: '0.5px' 
                  }}
                >
                  Search Items
                </Typography>
                <TextField
                  size="small"
                  placeholder={`Search ${selectedCategory?.category.toLowerCase() ?? 'items'}...`}
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                    ...(search && {
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => setSearch('')}
                            edge="end"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }),
                  }}
                  fullWidth
                  sx={{ '& .MuiOutlinedInput-root': { fontSize: '0.85rem' } }}
                />
              </Box>
            </Paper>
          </Grid>

          {/* RIGHT COLUMN: Selected Items List */}
          <Grid item xs={12} md={9}>
            {/* Header showing selected category + count */}
            {selectedCategory && (
              <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography variant="subtitle2" fontWeight={600} color="text.secondary">
                  SELECTED ITEMS
                </Typography>
                <Chip
                  label={`(${total})`}
                  size="small"
                  color="primary"
                  variant="outlined"
                />
              </Box>
            )}

            {/* Category Tabs / Pills showing all categories with counts */}
            <Box sx={{ mb: 3, display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              {categories.map((cat) => (
                <Chip
                  key={cat.id}
                  label={`${cat.category} (${itemCounts[cat.id] ?? 0})`}
                  onClick={() => { setSelectedCategoryId(cat.id); setSearch(''); }}
                  variant={selectedCategoryId === cat.id ? 'filled' : 'outlined'}
                  color={selectedCategoryId === cat.id ? 'primary' : 'default'}
                  clickable
                  sx={{
                    fontWeight: selectedCategoryId === cat.id ? 600 : 400,
                    fontSize: '0.9rem',
                  }}
                />
              ))}
            </Box>

            {/* Error */}
            {listError && <Alert severity="error" sx={{ mb: 2 }}>{listError}</Alert>}

            {/* Table of items */}
            <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
              <TableContainer>
                <Table>
                  <AppTableHead>
                    <TableRow>
                      <AppTableHeadCell>Item</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 120 }}>Created</AppTableHeadCell>
                      <AppTableHeadCell sx={{ width: 120 }}>Updated</AppTableHeadCell>
                      <AppTableHeadCell align="right" sx={{ width: 96 }}>Actions</AppTableHeadCell>
                    </TableRow>
                  </AppTableHead>
                  <TableBody>
                    {loading ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6 }}>
                          <CircularProgress size={24} />
                        </TableCell>
                      </TableRow>
                    ) : rows.length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={4} align="center" sx={{ py: 6, color: 'text.secondary' }}>
                          No items in this category
                        </TableCell>
                      </TableRow>
                    ) : (
                      rows.map((row, idx) => (
                        <AppTableRow key={row.id} sx={{ bgcolor: idx % 2 !== 0 ? 'action.hover' : 'transparent' }}>
                          <TableCell sx={{ fontWeight: 500 }}>{row.description}</TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                            {fmtDate(row.created_at)}
                          </TableCell>
                          <TableCell sx={{ color: 'text.secondary', fontSize: '0.8125rem' }}>
                            {fmtDate(row.updated_at)}
                          </TableCell>
                          <TableCell align="right">
                            <Tooltip title="Edit">
                              <IconButton size="small" onClick={() => void openEdit(row.id)}>
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => confirmDelete(row.id, row.description)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </TableCell>
                        </AppTableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </TableContainer>
            </Paper>
          </Grid>
        </Grid>
      </Container>

      {/* ------------------------------------------------------------------ */}
      {/* Add / Edit Dialog                                                    */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingId ? 'Edit Program Settings Item' : 'Add Program Settings Item'}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>
          {formError && <Alert severity="error">{formError}</Alert>}
          <TextField
            label="Description"
            size="small"
            fullWidth
            required
            placeholder="e.g. Fixed Irrigation"
            inputProps={{ maxLength: 300 }}
            value={form.description}
            onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
            autoFocus
          />
          <FormControl size="small" fullWidth>
            <InputLabel>Category</InputLabel>
            <Select
              label="Category"
              value={form.category_id}
              onChange={(e) => setForm((f) => ({ ...f, category_id: e.target.value as number | '' }))}
            >
              <MenuItem value=""><em>None</em></MenuItem>
              {categories.map((cat) => (
                <MenuItem key={cat.id} value={cat.id}>{cat.category}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={() => void handleSave()}
            disabled={saving}
            startIcon={saving ? <CircularProgress size={14} /> : undefined}
          >
            {editingId ? 'Save Changes' : 'Add Item'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ------------------------------------------------------------------ */}
      {/* Delete Confirmation                                                  */}
      {/* ------------------------------------------------------------------ */}
      <Dialog open={deleteId !== null} onClose={() => setDeleteId(null)} maxWidth="xs">
        <DialogTitle>Delete Program Settings Item</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete <strong>{deleteLabel}</strong>? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setDeleteId(null)}>Cancel</Button>
          <Button
            variant="contained"
            color="error"
            onClick={() => void handleDelete()}
            disabled={deleting}
            startIcon={deleting ? <CircularProgress size={14} /> : undefined}
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackOpen}
        autoHideDuration={3000}
        onClose={() => setSnackOpen(false)}
        message={snackMsg}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </>
  );
}
